import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plugs, Robot, CreditCard, CheckCircle, Warning, FloppyDisk, Lightning, Eye, EyeSlash } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVIDER_LABELS = {
  none: "Desativado (heuristica por regras)",
  openai: "OpenAI",
  anthropic: "Anthropic (Claude)",
  gemini: "Google (Gemini)",
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("blumove_token")}` };
}

export default function SuperAdminIntegrations() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState("none");
  const [model, setModel] = useState("");
  const [aiKey, setAiKey] = useState("");
  const [stripeKey, setStripeKey] = useState("");
  const [showAiKey, setShowAiKey] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/integrations`, { headers: authHeaders() });
      setCfg(r.data);
      setProvider(r.data.ai_provider || "none");
      setModel(r.data.ai_model || "");
    } catch (e) {
      setToast("Erro ao carregar integracoes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  const save = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const body = { ai_provider: provider, ai_model: model };
      if (aiKey.trim()) body.ai_api_key = aiKey.trim();
      if (stripeKey.trim()) body.stripe_api_key = stripeKey.trim();
      const r = await axios.put(`${API}/admin/integrations`, body, { headers: authHeaders() });
      setCfg(r.data);
      setAiKey("");
      setStripeKey("");
      flash("Guardado com sucesso.");
    } catch (e) {
      flash(e?.response?.data?.detail || "Erro ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async (which) => {
    setSaving(true);
    try {
      const body = which === "ai" ? { ai_api_key: "__CLEAR__" } : { stripe_api_key: "__CLEAR__" };
      const r = await axios.put(`${API}/admin/integrations`, body, { headers: authHeaders() });
      setCfg(r.data);
      flash("Chave removida.");
    } catch (e) {
      flash("Erro ao remover chave.");
    } finally {
      setSaving(false);
    }
  };

  const testAi = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/admin/integrations/test-ai`, {}, { headers: authHeaders() });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Falhou." });
    } finally {
      setTesting(false);
    }
  };

  const inputStyle = { width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", fontSize: "0.9rem", fontFamily: "Manrope" };
  const cardStyle = { background: "var(--surface)", borderRadius: 16, border: "1px solid var(--border-color)", padding: 22, marginBottom: 18 };

  if (loading) {
    return <div style={{ padding: "100px 20px", textAlign: "center", color: "var(--text-muted)" }}>A carregar...</div>;
  }

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "90px 20px 60px" }} data-testid="integrations-page">
      <div className="flex items-center gap-3" style={{ marginBottom: 6 }}>
        <Plugs size={26} weight="duotone" style={{ color: "var(--primary)" }} />
        <h1 style={{ fontFamily: "Outfit", fontWeight: 800, fontSize: "1.7rem" }}>Integracoes & APIs</h1>
      </div>
      <p style={{ color: "var(--text-muted)", fontSize: "0.88rem", marginBottom: 24 }}>
        Gira as chaves que dao vida a IA e aos pagamentos. As chaves ficam guardadas de forma segura e nunca sao mostradas por completo.
      </p>

      {toast && (
        <div data-testid="integrations-toast" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 10, background: "rgba(45,74,62,0.08)", color: "var(--primary)", fontSize: "0.85rem", fontWeight: 600 }}>{toast}</div>
      )}

      {/* AI card */}
      <div style={cardStyle}>
        <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
          <Robot size={20} weight="duotone" style={{ color: "var(--primary)" }} />
          <h2 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem" }}>Inteligencia Artificial</h2>
          <span style={{ marginLeft: "auto", fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px", borderRadius: 9999, background: cfg.ai_configured ? "rgba(22,163,74,0.12)" : "rgba(120,120,120,0.12)", color: cfg.ai_configured ? "#16a34a" : "var(--text-muted)" }}>
            {cfg.ai_configured ? "Configurado" : "Nao configurado"}
          </span>
        </div>
        <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", marginBottom: 16 }}>
          Fornecedor usado para recomendacoes de rota. Sem chave, usa uma heuristica local (funciona sempre, sem custos).
        </p>

        <label className="overline" style={{ display: "block", marginBottom: 6 }}>Fornecedor</label>
        <select data-testid="ai-provider-select" value={provider} onChange={(e) => setProvider(e.target.value)} style={{ ...inputStyle, marginBottom: 14 }}>
          {Object.keys(PROVIDER_LABELS).map((p) => (
            <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>
          ))}
        </select>

        {provider !== "none" && (
          <>
            <label className="overline" style={{ display: "block", marginBottom: 6 }}>Modelo</label>
            <input data-testid="ai-model-input" value={model} onChange={(e) => setModel(e.target.value)}
              placeholder={cfg.default_models?.[provider] || "modelo"} style={{ ...inputStyle, marginBottom: 14 }} />

            <label className="overline" style={{ display: "block", marginBottom: 6 }}>Chave API {cfg.ai_configured && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(atual: {cfg.ai_key_masked})</span>}</label>
            <div style={{ position: "relative", marginBottom: 8 }}>
              <input data-testid="ai-key-input" type={showAiKey ? "text" : "password"} value={aiKey} onChange={(e) => setAiKey(e.target.value)}
                placeholder={cfg.ai_configured ? "Deixe vazio para manter a atual" : "Cole a chave API"} style={{ ...inputStyle, paddingRight: 42 }} />
              <button type="button" onClick={() => setShowAiKey(s => !s)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>
                {showAiKey ? <EyeSlash size={17} /> : <Eye size={17} />}
              </button>
            </div>
            <div className="flex items-center gap-3" style={{ marginBottom: 4 }}>
              <button data-testid="ai-test-btn" onClick={testAi} disabled={testing || !cfg.ai_configured} className="flex items-center gap-1"
                style={{ background: "none", border: "1px solid var(--border-color)", borderRadius: 9999, padding: "6px 14px", cursor: cfg.ai_configured ? "pointer" : "not-allowed", fontSize: "0.78rem", fontWeight: 600, opacity: cfg.ai_configured ? 1 : 0.5 }}>
                <Lightning size={14} weight="fill" /> {testing ? "A testar..." : "Testar ligacao"}
              </button>
              {cfg.ai_configured && (
                <button onClick={() => clearKey("ai")} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600 }}>Remover chave</button>
              )}
            </div>
            {testResult && (
              <div className="flex items-start gap-2" data-testid="ai-test-result" style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, fontSize: "0.8rem", background: testResult.ok ? "rgba(22,163,74,0.08)" : "rgba(220,38,38,0.08)", color: testResult.ok ? "#16a34a" : "#dc2626" }}>
                {testResult.ok ? <CheckCircle size={16} weight="fill" /> : <Warning size={16} weight="fill" />}
                <span style={{ wordBreak: "break-word" }}>{testResult.ok ? `Ligacao OK. Resposta: ${testResult.sample || "(vazia)"}` : `Falhou: ${testResult.error}`}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Stripe card */}
      <div style={cardStyle}>
        <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
          <CreditCard size={20} weight="duotone" style={{ color: "var(--primary)" }} />
          <h2 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem" }}>Pagamentos (Stripe)</h2>
          <span style={{ marginLeft: "auto", fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px", borderRadius: 9999, background: cfg.stripe_configured ? "rgba(22,163,74,0.12)" : "rgba(120,120,120,0.12)", color: cfg.stripe_configured ? "#16a34a" : "var(--text-muted)" }}>
            {cfg.stripe_configured ? "Configurado" : "Nao configurado"}
          </span>
        </div>
        <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", marginBottom: 16 }}>
          Chave secreta Stripe (sk_...). Sem chave, o pagamento fica desativado graciosamente.
        </p>
        <label className="overline" style={{ display: "block", marginBottom: 6 }}>Chave secreta {cfg.stripe_configured && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(atual: {cfg.stripe_key_masked})</span>}</label>
        <div style={{ position: "relative", marginBottom: 8 }}>
          <input data-testid="stripe-key-input" type={showStripeKey ? "text" : "password"} value={stripeKey} onChange={(e) => setStripeKey(e.target.value)}
            placeholder={cfg.stripe_configured ? "Deixe vazio para manter a atual" : "sk_live_... ou sk_test_..."} style={{ ...inputStyle, paddingRight: 42 }} />
          <button type="button" onClick={() => setShowStripeKey(s => !s)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>
            {showStripeKey ? <EyeSlash size={17} /> : <Eye size={17} />}
          </button>
        </div>
        {cfg.stripe_configured && (
          <button onClick={() => clearKey("stripe")} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600 }}>Remover chave</button>
        )}
      </div>

      <button data-testid="integrations-save-btn" onClick={save} disabled={saving} className="flex items-center justify-center gap-2"
        style={{ width: "100%", padding: "14px", borderRadius: 12, border: "none", background: "var(--primary)", color: "#fff", fontWeight: 700, fontSize: "0.95rem", cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}>
        <FloppyDisk size={18} weight="fill" /> {saving ? "A guardar..." : "Guardar alteracoes"}
      </button>
    </div>
  );
}
